import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import { 
  Popover, 
  IconButton, 
  TextField, 
  Box, 
  Typography, 
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";

const RegistrationTable = () => {
  const { eventId } = useParams();
  const [registrations, setRegistrations] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    phone: "",
    startDate: "",
    endDate: ""
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/eventregistrations/${eventId}`);
        setRegistrations(res.data);
      } catch (err) {
        console.error("Failed to fetch registrations:", err);
      }
    };
    fetchRegistrations();
  }, [eventId]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/eventregistrations/${id}`);
      setRegistrations(registrations.filter((r) => r._id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const openPopover = async (event, registration) => {
    setAnchorEl(event.currentTarget);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/events/${registration.eventId}`);
      setSelectedEvent(response.data);
    } catch (err) {
      console.error("Failed to fetch event details:", err);
      setSelectedEvent({ name: "Not found" });
    }
  };

  const closePopover = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const filteredRegistrations = registrations.filter((reg) => {
    const date = new Date(reg.createdAt);
    const start = filters.startDate ? new Date(filters.startDate) : null;
    const end = filters.endDate ? new Date(filters.endDate) : null;

    return (
      reg.name.toLowerCase().includes(filters.name.toLowerCase()) &&
      reg.email.toLowerCase().includes(filters.email.toLowerCase()) &&
      reg.phone.includes(filters.phone) &&
      (!start || date >= start) &&
      (!end || date <= end)
    );
  });

  return (
    <div className="min-h-screen bg-white p-6">
      <h2 className="text-3xl font-bold mb-6 text-indigo-600">Event Registrations</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <TextField 
          label="Name" 
          size="small" 
          value={filters.name} 
          onChange={(e) => setFilters({ ...filters, name: e.target.value })} 
        />
        <TextField 
          label="Email" 
          size="small" 
          value={filters.email} 
          onChange={(e) => setFilters({ ...filters, email: e.target.value })} 
        />
        <TextField 
          label="Phone" 
          size="small" 
          value={filters.phone} 
          onChange={(e) => setFilters({ ...filters, phone: e.target.value })} 
        />
        <TextField 
          type="date" 
          size="small" 
          label="Start Date" 
          InputLabelProps={{ shrink: true }} 
          value={filters.startDate} 
          onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} 
        />
        <TextField 
          type="date" 
          size="small" 
          label="End Date" 
          InputLabelProps={{ shrink: true }} 
          value={filters.endDate} 
          onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} 
        />
      </div>

      <TableContainer component={Paper} className="shadow-xl rounded-xl">
        <Table>
          <TableHead>
            <TableRow className="bg-indigo-100">
              <TableCell className="font-semibold">Registered at</TableCell>
              <TableCell className="font-semibold">Event Name</TableCell>
              <TableCell className="font-semibold">Name</TableCell>
              <TableCell className="font-semibold">Email</TableCell>
              <TableCell className="font-semibold">Phone</TableCell>
              <TableCell className="font-semibold">User Type</TableCell>
              <TableCell className="font-semibold">Event Type</TableCell>
              {registrations.some(reg => reg.userType === 'founder') && (
                <>
                  <TableCell className="font-semibold">Founder ID</TableCell>
                  <TableCell className="font-semibold">Startup Name</TableCell>
                  <TableCell className="font-semibold">Industry</TableCell>
                  <TableCell className="font-semibold">Location</TableCell>
                </>
              )}
              <TableCell className="font-semibold">Action</TableCell>
            </TableRow>
          </TableHead>
          <AnimatePresence>
            <TableBody>
              {filteredRegistrations.map((reg) => (
                <motion.tr
                  key={reg._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="hover:bg-indigo-50"
                >
                  <TableCell>{new Date(reg.createdAt).toLocaleString()}</TableCell>
                  <TableCell className="flex items-center gap-1">
                    {reg.eventName}
                    <IconButton size="small" onClick={(e) => openPopover(e, reg)}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell>{reg.name}</TableCell>
                  <TableCell>{reg.email}</TableCell>
                  <TableCell>{reg.phone}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      reg.userType === "individual" ? "bg-blue-200 text-blue-800" : "bg-purple-200 text-purple-800"
                    }`}>
                      {reg.userType}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      reg.eventType === "Free" ? "bg-green-200 text-green-800" : "bg-yellow-200 text-yellow-800"
                    }`}>
                      {reg.eventType}
                    </span>
                  </TableCell>
                  {registrations.some(r => r.userType === 'founder') && (
                    <>
                      <TableCell>{reg.userType === 'founder' ? reg.founderId : '-'}</TableCell>
                      <TableCell>{reg.userType === 'founder' ? reg.startupName : '-'}</TableCell>
                      <TableCell>{reg.userType === 'founder' ? reg.industry : '-'}</TableCell>
                      <TableCell>{reg.userType === 'founder' ? reg.startupLocation : '-'}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDelete(reg._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </AnimatePresence>
        </Table>
        {filteredRegistrations.length === 0 && (
          <Box className="text-center text-gray-500 py-4">
            <Typography>No registrations found.</Typography>
          </Box>
        )}
      </TableContainer>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          p={3}
          sx={{
            maxWidth: 700,
            bgcolor: '#f9fafb',
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Event Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Name"
                value={selectedEvent?.name || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Title"
                value={selectedEvent?.title || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={selectedEvent?.description || ''}
                variant="outlined"
                size="small"
                multiline
                rows={3}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Type"
                value={selectedEvent?.type || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Status"
                value={selectedEvent?.status || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Amount"
                value={selectedEvent?.amount || 'Free'}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Payment Mode"
                value={selectedEvent?.mode || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Date"
                value={selectedEvent?.date || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Time"
                value={selectedEvent?.time || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Location"
                value={selectedEvent?.location || 'Online'}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Link"
                value={selectedEvent?.link || ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </div>
  );
};

export default RegistrationTable;